import { IDisposable } from "monaco-editor-core";

import {
  BoundingBox,
  boundingBoxCenter,
  boundingBoxHeight,
  boundingBoxOffset,
  boundingBoxWidth
} from "../../Data/Geometry/BoundingBox";
import { Vec2, vec2Add } from "../../Data/Geometry/Vec2";
import { Panic } from "../../Errors/Panic";
import { MenuPopup } from "./ContextMenuPopup";
import { DiagnosticsPopup } from "./DiagnosticsPopup";
import styles from "./EditorContextPopup.module.css";
import { InfoPopup } from "./InfoPopup";

export declare namespace EditorContextPopup {
  export interface Events {
    onDismiss: () => void;
    onCutClick: () => void;
    onCopyClick: () => void;
    onPasteClick: () => void;
  }
}

export class EditorContextPopup<DiagnosticId> implements IDisposable {
  constructor(
    position: Vec2,
    diagnostics: DiagnosticsPopup.Diagnostic<DiagnosticId>[] | null,
    private readonly listener: EditorContextPopup.Events
  ) {
    console.log("EditorContextPopup", position);

    // Create Backdrop

    this.backdrop = createBackdrop({
      onDismiss: () => {
        this.listener.onDismiss();
      }
    });
    document.body.appendChild(this.backdrop);

    // Create DiagnosticsPopup

    const diagnosticsPopupSize = DiagnosticsPopup.getSize();

    const diagnosticsPopupOffset = {
      x: -Math.floor(diagnosticsPopupSize.width / 2),
      y: -144
    };

    const dialogBoxBoundingBox = boundingBoxOffset(
      {
        left: 0,
        top: 0,
        right: diagnosticsPopupSize.width,
        bottom: diagnosticsPopupSize.height
      },
      vec2Add(diagnosticsPopupOffset, position)
    );

    const fullBoundingBox = dialogBoxBoundingBox;

    const targetOffset = clampInside(fullBoundingBox, {
      left: 0,
      top: 0,
      right: window.innerWidth,
      bottom: window.innerHeight
    });

    const targetPos: Vec2 = vec2Add(position, targetOffset);

    this.target = createTarget(targetPos);
    document.body.appendChild(this.target);

    const diagnosticsPopupPosition = vec2Add(targetPos, diagnosticsPopupOffset);

    const diagnosticsPopup = new DiagnosticsPopup<DiagnosticId>(
      diagnosticsPopupPosition,
      diagnostics,
      {
        onExpand: () => {
          if (this.infoPopup === null) {
            throw new Panic("infoPopup === null", {});
          }
          this.infoPopup.dispose();
          this.infoPopup = null;
          if (this.menuPopup === null) {
            throw new Panic("menuPopup === null", {});
          }
          this.menuPopup.dispose();
          this.menuPopup = null;

          if (this.target === null) {
            throw new Panic("this.target === null", {});
          }
          document.body.removeChild(this.target);
          this.target = null;
        },
        onLeftButtonClick: () => {
          if (diagnostics === null) {
            return;
          }
          let index = diagnosticsPopup.getCurrentDiagnosticIndex();
          index--;
          if (index < 0) {
            index = diagnostics.length - 1;
          }
          diagnosticsPopup.setCurrentDiagnosticIndex(index);
        },
        onRightButtonClick: () => {
          if (diagnostics === null) {
            return;
          }
          let index = diagnosticsPopup.getCurrentDiagnosticIndex();
          index++;
          if (index >= diagnostics.length) {
            index = 0;
          }
          diagnosticsPopup.setCurrentDiagnosticIndex(index);
        }
      }
    );
    this.diagnosticsPopup = diagnosticsPopup;

    // Create InfoPopup

    // const infoPopupSize = InfoPopup.getSize();

    const infoPopupOffset = {
      x: 192,
      y: -30
    };

    this.infoPopup = new InfoPopup(vec2Add(targetPos, infoPopupOffset), {
      onExpand: () => {
        if (this.diagnosticsPopup === null) {
          throw new Panic("diagnosticsPopup === null", {});
        }
        this.diagnosticsPopup.dispose();
        this.diagnosticsPopup = null;
        if (this.menuPopup === null) {
          throw new Panic("menuPopup === null", {});
        }
        this.menuPopup.dispose();
        this.menuPopup = null;

        if (this.target === null) {
          throw new Panic("this.target === null", {});
        }
        document.body.removeChild(this.target);
        this.target = null;
      }
    });

    // Create MenuPopup

    this.menuPopup = new MenuPopup(
      {
        x: targetPos.x - 180,
        y: targetPos.y + 64
      },
      {
        onCutClick: listener.onCutClick,
        onCopyClick: listener.onCopyClick,
        onPasteClick: listener.onPasteClick
      }
    );

    this.menuPopup.getSize();
  }

  public setInfoData(infoData: InfoPopup.InfoData): void {
    if (this.infoPopup !== null) {
      this.infoPopup.setInfoData(infoData);
    }
  }

  private backdrop: HTMLElement;
  private target: HTMLElement | null;
  private diagnosticsPopup: DiagnosticsPopup<DiagnosticId> | null;
  private infoPopup: InfoPopup | null;
  private menuPopup: MenuPopup | null;

  dispose(): void {
    if (this.menuPopup !== null) {
      this.menuPopup.dispose();
    }
    if (this.infoPopup !== null) {
      this.infoPopup.dispose();
    }
    if (this.diagnosticsPopup !== null) {
      this.diagnosticsPopup.dispose();
    }
    if (this.target !== null) {
      document.body.removeChild(this.target);
    }
    document.body.removeChild(this.backdrop);
  }
}

interface BackdropEvents {
  onDismiss: () => void;
}

function createBackdrop(backdropEvents: BackdropEvents): HTMLElement {
  const backdrop = document.createElement("div");
  backdrop.className = styles.backdrop;

  backdrop.addEventListener("click", (event) => {
    backdropEvents.onDismiss();
  });
  backdrop.addEventListener("contextmenu", (event) => {
    event.stopPropagation();
    event.preventDefault();
    event.stopImmediatePropagation();
    backdropEvents.onDismiss();
  });

  return backdrop;
}

function createTarget(position: Vec2): HTMLElement {
  const targetCont = document.createElement("div");
  targetCont.className = styles.target_cont;
  targetCont.style.left = "" + position.x + "px";
  targetCont.style.top = "" + position.y + "px";

  const target = document.createElement("div");
  target.className = styles.target;
  targetCont.appendChild(target);

  const topLine = document.createElement("div");
  topLine.className = styles.target_line;
  topLine.style.width = "0px";
  topLine.style.height = "64px";
  topLine.style.left = "-1px";
  topLine.style.top = "-79px";
  targetCont.appendChild(topLine);

  const rightLine = document.createElement("div");
  rightLine.className = styles.target_line;
  rightLine.style.width = "176px";
  rightLine.style.height = "0px";
  rightLine.style.left = "16px";
  rightLine.style.top = "-1px";
  targetCont.appendChild(rightLine);

  const bottomLine = document.createElement("div");
  bottomLine.className = styles.target_line;
  bottomLine.style.width = "0px";
  bottomLine.style.height = "64px";
  bottomLine.style.left = "-1px";
  bottomLine.style.top = "16px";
  targetCont.appendChild(bottomLine);

  return targetCont;
}

export function clampInside(boundingBox: BoundingBox, container: BoundingBox): Vec2 {
  let x = 0;
  if (boundingBoxWidth(boundingBox) >= boundingBoxWidth(container)) {
    x = boundingBoxCenter(container).x - boundingBoxCenter(boundingBox).x;
  } else if (boundingBox.left < container.left) {
    x = container.left - boundingBox.left;
  } else if (boundingBox.right > container.right) {
    x = container.right - boundingBox.right;
  }

  let y = 0;
  if (boundingBoxHeight(boundingBox) >= boundingBoxHeight(container)) {
    y = boundingBoxCenter(container).y - boundingBoxCenter(boundingBox).y;
  } else if (boundingBox.top < container.top) {
    y = container.top - boundingBox.top;
  } else if (boundingBox.bottom > container.bottom) {
    y = container.bottom - boundingBox.bottom;
  }

  return {
    x: x,
    y: y
  };
}
